import { Col, Divider, Form, type FormInstance, Input, Row, Typography } from 'antd';
import YoutubePlayer from 'youtube-video-element/react';
import type {
    FormProdutoFoto,
    FormProdutoValues,
} from '@/features/produto/types/FormProdutoValues';
import { Container } from '@/components/Container';
import { ImageList, type ImageListValue } from '@/components/image/image-list/ImageList';

const { Text } = Typography;

export function ProdutoTabFotoVideo({
    form,
    setFormDirty,
}: {
    form: FormInstance<FormProdutoValues>;
    setFormDirty: (dirty: boolean) => void;
}) {
    const produtoFotos: FormProdutoFoto[] =
        Form.useWatch('produto_foto', { form, preserve: true }) ?? [];

    const pro_video_url = Form.useWatch('pro_video_url', form);

    const imageList: ImageListValue[] = produtoFotos
        .filter((foto) => foto.excluir !== 'S')
        .map((foto) => ({
            idpk: String(foto.key),
            path: foto.foto_preview ?? foto.prf_foto_path ?? '',
            principal: foto.prf_foto_principal === 1,
        }));

    const updateFotos = (fotos: FormProdutoFoto[]): void => {
        form.setFieldValue('produto_foto', fotos);
        setFormDirty(true);
    };

    const imageListChange = async (
        key: string,
        fileName: string,
        base64: string,
        preview: string,
    ): Promise<number | null> => {
        const fotosAtuais: FormProdutoFoto[] = form.getFieldValue('produto_foto') ?? [];
        updateFotos([
            ...fotosAtuais,
            {
                alterar: true,
                foto_base64: base64,
                foto_nome: fileName,
                foto_preview: preview,
                key,
                prf_descricao: '',
                prf_foto_principal: fotosAtuais.every((foto) => foto.excluir === 'S') ? 1 : 0,
            },
        ]);

        return null;
    };

    const imageListDelete = (idFoto: string): void => {
        const fotosAtuais: FormProdutoFoto[] = form.getFieldValue('produto_foto') ?? [];

        const excluindoPrincipal = fotosAtuais.some(
            (foto) => String(foto.key) === idFoto && foto.prf_foto_principal === 1,
        );

        const restantes = fotosAtuais.flatMap((foto): FormProdutoFoto[] => {
            if (String(foto.key) !== idFoto) {
                return [foto];
            }
            return foto.prf_idpk ? [{ ...foto, excluir: 'S' }] : [];
        });

        const novaPrincipal = restantes.find((foto) => foto.excluir !== 'S');

        updateFotos(
            excluindoPrincipal
                ? restantes.map((foto) => {
                      const fotoPrincipal = foto === novaPrincipal ? 1 : 0;
                      if (foto.excluir === 'S' || foto.prf_foto_principal === fotoPrincipal) {
                          return foto;
                      }
                      return { ...foto, alterar: true, prf_foto_principal: fotoPrincipal };
                  })
                : restantes,
        );
    };

    const imageListUpdate = async (id: string, principal: boolean): Promise<boolean> => {
        const fotosAtuais: FormProdutoFoto[] = form.getFieldValue('produto_foto') ?? [];

        updateFotos(
            fotosAtuais.map((foto) => {
                const fotoPrincipal =
                    String(foto.key) === id
                        ? principal
                            ? 1
                            : 0
                        : principal
                          ? 0
                          : foto.prf_foto_principal;

                if (foto.excluir === 'S' || foto.prf_foto_principal === fotoPrincipal) {
                    return foto;
                }

                return { ...foto, alterar: true, prf_foto_principal: fotoPrincipal };
            }),
        );

        return true;
    };

    return (
        <>
            <Container className='mb-2'>
                <Row gutter={[8, 8]}>
                    <Col lg={16} md={12} sm={24} xs={24}>
                        <Form.Item
                            help='Preencha com a URL do vídeo de apresentação de seu produto. Este vídeo pode ser
                    hospedado no youtube, vimeo, dailymotion ou qualquer outra plataforma de
                    armazenamento.'
                            label='Link do vídeo'
                            name='pro_video_url'
                        >
                            <Input />
                        </Form.Item>
                    </Col>

                    {pro_video_url && (
                        <Col lg={8} md={12} sm={24} xs={24}>
                            <div className='relative' style={{ paddingTop: '56.25%' }}>
                                <YoutubePlayer
                                    className='absolute left-0 top-0 h-full w-full shadow-xl'
                                    controls={true}
                                    src={pro_video_url}
                                />
                            </div>
                        </Col>
                    )}
                </Row>
            </Container>

            <Container>
                <Row>
                    <Col offset={0}>
                        <div className='header'>
                            <Text>Adicione até 5 imagens ao cadastro do produto.</Text>
                            <p className='text-gray-500'>
                                Dimensões recomendadas: <b>512x512</b> ou <b>768x768</b>
                            </p>
                            <Text type='secondary'>
                                A foto selecionada em destaque é a foto principal do produto.
                            </Text>
                        </div>
                    </Col>
                </Row>

                <Divider />

                <ImageList
                    onChange={imageListChange}
                    onDelete={imageListDelete}
                    onUpdate={imageListUpdate}
                    value={imageList}
                />
            </Container>
        </>
    );
}
